/**
 * The achievement registry — id, display copy, tier, exp and atlas icon key
 * for every achievement in the game: the original bronze batch plus batch 2
 * (Let's Go Gambling, nine silver, six gold), The Hub's batch, and the Diamond
 * tier (2026-10-29: eight new, plus Chiply's Anger Issues upgraded from
 * silver) with the bronze Gearbox.
 *
 * The server-side allowlist in dev_give (supabase/migrations) must match the
 * ids here (plus the 'reset'/'all' pseudo-values), the same way FEATURE_FLAGS
 * mirrors its unlock allowlist.
 * Specs: docs/superpowers/specs/2026-08-25-achievements-ranks-design.md,
 *        docs/superpowers/specs/2026-08-26-achievements-batch2-phantom-design.md
 */

export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'diamond';

export interface Achievement {
  id: string;
  name: string;
  tier: AchievementTier;
  /** Atlas texture key for the badge icon. */
  icon: string;
  /** "How you got it" — shown in the toast and the detail panel. */
  how: string;
  /**
   * A bronze achievement whose `how` stays "???" until The Hub is unlocked
   * (Scientist — the Potion Shop is a Hub thing). Silver and gold are always
   * secret until earned; this is the one bronze exception.
   */
  hiddenUntil?: 'hub';
  /**
   * Limited time: earnable only while that event runs (`originEventLive()`),
   * kept forever once earned. The detail panel says so.
   */
  limited?: 'origin';
}

/** Exp an achievement pays, by tier. */
export const ACHIEVEMENT_EXP: Record<AchievementTier, number> = {
  bronze: 200,
  silver: 300,
  gold: 500,
  diamond: 750,
};

export const ACHIEVEMENTS = [
  {
    id: 'firstkill',
    name: 'First Kill',
    tier: 'bronze',
    icon: 'ach-firstkill',
    how: 'Defeat your first enemy',
  },
  {
    id: 'stageness',
    name: 'Stageness',
    tier: 'bronze',
    icon: 'ach-stageness',
    how: 'Complete your first episode',
  },
  {
    id: 'tester',
    name: 'Tester',
    tier: 'bronze',
    icon: 'ach-tester',
    how: 'Complete a wave in Test mode',
  },
  {
    id: 'verybossy',
    name: 'Very Bossy',
    tier: 'bronze',
    icon: 'ach-verybossy',
    how: 'Defeat the Golden Nugget at the end of its episode',
  },
  {
    id: 'shopper',
    name: 'Shopper',
    tier: 'bronze',
    icon: 'ach-shopper',
    how: 'Buy a tower from the Shop',
  },
  {
    id: 'failure',
    name: 'FAILURE',
    tier: 'bronze',
    icon: 'ach-failure',
    how: 'Lose an episode — the Picnic fell',
  },
  {
    id: 'maxtier',
    name: 'Max Tier',
    tier: 'bronze',
    icon: 'ach-maxtier',
    how: 'Upgrade a tower to tier 3',
  },
  // ── batch 2 (spec: 2026-08-26-achievements-batch2-phantom-design.md) ──────
  {
    id: 'gambling',
    name: "Let's Go Gambling",
    tier: 'bronze',
    icon: 'ach-gambling',
    how: 'Open your first takeout bag',
  },
  {
    id: 'null',
    name: 'Null',
    tier: 'silver',
    icon: 'ach-null',
    how: 'Beat The Null Nugget',
  },
  {
    id: 'evolution',
    name: 'Evolution',
    tier: 'silver',
    icon: 'ach-evolution',
    how: 'Evolve a tower',
  },
  {
    id: 'bewise',
    name: 'Be Wise',
    tier: 'silver',
    icon: 'ach-bewise',
    how: 'Unlock Wise Hambi',
  },
  {
    id: 'loadout',
    name: 'Loadout',
    tier: 'silver',
    icon: 'ach-loadout',
    how: 'Have 10/10 towers in your loadout',
  },
  {
    id: 'letitburn',
    name: 'let it burn',
    tier: 'silver',
    icon: 'ach-letitburn',
    how: 'Get the Fire Mage',
  },
  {
    id: 'aquatic',
    name: 'Aquatic Venture',
    tier: 'silver',
    icon: 'ach-aquatic',
    how: 'Get the Aqua Sorcerer',
  },
  {
    id: 'doubles',
    name: 'Doubles',
    tier: 'silver',
    icon: 'ach-doubles',
    how: 'Roll the same tower twice in a row from the takeout bag',
  },
  {
    id: 'itrustedyou',
    name: 'I Trusted you!',
    tier: 'silver',
    icon: 'ach-itrustedyou',
    how: 'Lose 10 times',
  },
  {
    id: 'richness',
    name: 'Richness',
    tier: 'silver',
    icon: 'ach-richness',
    how: 'Hold 500 hambi coins',
  },
  {
    id: 'theveg',
    name: 'The Veg',
    tier: 'gold',
    icon: 'ach-theveg',
    how: 'Complete Throne of Nugget-tron',
  },
  {
    id: 'shoplifter',
    name: 'Shoplifter',
    tier: 'gold',
    icon: 'ach-shoplifter',
    how: 'Own every tower the Shop sells — both pages',
  },
  {
    id: 'spicy',
    name: 'Spicy!',
    tier: 'gold',
    icon: 'ach-spicy',
    how: "Complete The Emperor's Temple",
  },
  {
    id: 'waver',
    name: 'Waver',
    tier: 'gold',
    icon: 'ach-waver',
    how: 'Beat 100 waves in total',
  },
  {
    id: 'mansion',
    name: 'Mansion',
    tier: 'gold',
    icon: 'ach-mansion',
    how: 'Hold 1500 hambi coins',
  },
  {
    id: 'kingdown',
    name: 'King Down',
    tier: 'gold',
    icon: 'ach-kingdown',
    how: 'Complete The Ember and Flame — King Ember falls',
  },
  // ── The Hub batch (spec: 2026-09-02-the-hub-design.md, Revision 6) ────────
  {
    id: 'welcomehub',
    name: 'Welcome to the Hub',
    tier: 'bronze',
    icon: 'ach-welcomehub',
    how: 'Enter The Hub',
  },
  {
    id: 'scientist',
    name: 'Scientist',
    tier: 'bronze',
    icon: 'ach-scientist',
    how: 'Buy a potion from the Potion Shop',
    hiddenUntil: 'hub',
  },
  {
    id: 'angerissues',
    name: "Chiply's Anger Issues",
    // Silver until 2026-10-29, when it was upgraded to Diamond (no back-pay).
    tier: 'diamond',
    icon: 'ach-angerissues',
    how: 'Lose a run to Chiply — he takes your whole base in one bite',
  },
  {
    id: 'ko',
    name: 'K.O',
    tier: 'silver',
    icon: 'ach-ko',
    how: "Miss one of Chiply's Dad's chips and take the hit",
  },
  {
    id: 'sales',
    name: 'Sales!',
    tier: 'silver',
    icon: 'ach-sales',
    how: "Buy a tower from The Hub's Market",
  },
  {
    id: 'chipsociety',
    name: 'The Chip Society',
    tier: 'gold',
    icon: 'ach-chipsociety',
    how: "Complete The Space Station — Chiply's Dad falls",
  },
  {
    id: 'poisonedluck',
    name: 'Poisoned luck',
    tier: 'silver',
    icon: 'ach-poisonedluck',
    how: 'Bow Hambi looses a poison arrow',
  },
  // ── Diamond (spec: 2026-10-29-diamond-ranks-design.md) ────────────────────
  {
    id: 'quester',
    name: 'Quester',
    tier: 'diamond',
    icon: 'ach-quester',
    how: 'Claim all 3 daily quests 5 days in a row',
  },
  {
    id: 'thestink',
    name: 'The Stink',
    tier: 'diamond',
    icon: 'ach-thestink',
    how: 'Beat Through Space and Time and open The Fungi Forest',
  },
  {
    id: 'mushmillenium',
    name: 'Mush Millenium',
    tier: 'diamond',
    icon: 'ach-mushmillenium',
    how: 'Beat Mushy Mayhem',
  },
  {
    id: 'shroombellionfallen',
    name: 'Shroombellion has fallen',
    tier: 'diamond',
    icon: 'ach-shroombellionfallen',
    how: 'Beat The Shroombellion — King Shroometron falls',
  },
  {
    id: 'friendlyfungi',
    name: 'Friendly Fungi',
    tier: 'diamond',
    icon: 'ach-friendlyfungi',
    how: 'Win The Golden Shroom by completing Pathway of the Fungi',
  },
  {
    id: 'ghosting',
    name: 'Ghosting',
    tier: 'diamond',
    icon: 'ach-ghosting',
    how: 'Buy all three towers in the Dark Shop',
    limited: 'origin',
  },
  {
    id: 'halohunting',
    name: 'Halo Hunting',
    tier: 'diamond',
    icon: 'ach-halohunting',
    how: 'Buy all three towers in the Light Shop',
    limited: 'origin',
  },
  {
    id: 'kingoftheboard',
    name: 'King of the board',
    tier: 'diamond',
    icon: 'ach-kingoftheboard',
    how: 'Reach #1 on any leaderboard',
  },
  {
    id: 'gearbox',
    name: 'Gearbox',
    tier: 'bronze',
    icon: 'ach-gearbox',
    how: 'Flip a toggle in Settings',
  },
] as const satisfies readonly Achievement[];

export type AchievementId = (typeof ACHIEVEMENTS)[number]['id'];

export function isAchievementId(x: unknown): x is AchievementId {
  return typeof x === 'string' && ACHIEVEMENTS.some((a) => a.id === x);
}

export function achievementById(id: AchievementId): Achievement {
  return ACHIEVEMENTS.find((a) => a.id === id)!;
}

/**
 * The how-to text a viewer may see. Silver, gold and diamond achievements are secret
 * until earned — the spec's "???" rule; bronze is always visible, except a
 * `hiddenUntil: 'hub'` bronze, which reads "???" until The Hub is unlocked
 * (`hubOpen`).
 */
export function visibleHow(a: Achievement, earned: boolean, hubOpen = true): string {
  if (earned) return a.how;
  if (a.tier !== 'bronze') return '???';
  if (a.hiddenUntil === 'hub' && !hubOpen) return '???';
  return a.how;
}
